import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { LocalRelative } from "@/components/ui/local-date";
import { api } from "@/lib/api";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * The list of saved AI analyses (newest first), filterable by source. Lives
 * as the "History" tab on the Sources page; each item opens its full
 * analysis at `/analysis/history/$id`.
 */

type SavedAnalysis = {
  id: string;
  sourceType: "meeting" | "whatsapp" | "email" | "discord";
  sourceLabel: string | null;
  summary: string;
  createdAt: string;
  author: { id: string; name: string | null } | null;
  suggestionCount: number;
  approvedCount: number;
};

const FILTERS = ["all", "email", "whatsapp", "discord", "meeting"] as const;
type Filter = (typeof FILTERS)[number];

export function AnalysisHistoryList({
  source,
  emptyLabel,
  reloadKey = 0,
}: {
  /** Lock the list to one source (hides the filter tabs). */
  source?: "meeting" | "whatsapp" | "email" | "discord";
  emptyLabel?: string;
  /** Bump to refetch (e.g. after a new analysis was added). */
  reloadKey?: number;
} = {}) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>(source ?? "all");
  const [analyses, setAnalyses] = useState<SavedAnalysis[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const active: Filter = source ?? filter;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is an intentional refetch trigger, not read in the body
  useEffect(() => {
    let alive = true;
    setAnalyses(null);
    const qs = active === "all" ? "" : `?source=${active}`;
    api
      .get<{ analyses: SavedAnalysis[] }>(`/api/analysis${qs}`)
      .then((res) => {
        if (alive) setAnalyses(res.analyses);
      })
      .catch(() => {
        if (alive) setError(t("analysisTools.historyList.loadFailed"));
      });
    return () => {
      alive = false;
    };
  }, [active, reloadKey, t]);

  return (
    <div className="space-y-4">
      {!source && (
        <div className="flex gap-1">
          {FILTERS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              data-testid={`history-filter-${key}`}
              className={`rounded-md px-2.5 py-1 text-sm font-medium ${
                filter === key ? "bg-secondary text-foreground" : "text-muted-foreground"
              }`}
            >
              {t(`analysisTools.historyList.filters.${key}`)}
            </button>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
      {analyses && analyses.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="history-empty">
          {emptyLabel ??
            (active === "email"
              ? t("analysisTools.historyList.emptyEmail")
              : t("analysisTools.historyList.empty"))}
        </p>
      )}

      <div className="space-y-3">
        {analyses?.map((a) => (
          <Link
            key={a.id}
            to="/analysis/history/$id"
            params={{ id: a.id }}
            data-testid="history-item"
            className="block"
          >
            <Card className="transition-colors hover:bg-muted/40">
              <CardContent className="space-y-2 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="secondary">
                    {t(`analysisTools.historyList.sources.${a.sourceType}`)}
                  </Badge>
                  {a.sourceLabel && (
                    <span className="text-xs text-muted-foreground">{a.sourceLabel}</span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {t("analysisTools.historyList.applied", {
                      approved: a.approvedCount,
                      total: a.suggestionCount,
                    })}
                  </span>
                  <span className="text-xs text-muted-foreground">·</span>
                  <span className="text-xs text-muted-foreground">
                    <LocalRelative value={a.createdAt} />
                  </span>
                  {a.author?.name && (
                    <span className="text-xs text-muted-foreground">· {a.author.name}</span>
                  )}
                </div>
                <p className="text-sm line-clamp-2">
                  {a.summary || t("analysisTools.historyList.noSummary")}
                </p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
